import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import { deleteMyList, deleteSCList } from "components/features/Corp/CorpUser.Slice";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { apiRequest } from "services/ServiceUtil";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";

const SLICE = "CorpFilters";
const initialState: any = {
  searchText: "",
  filterCategories: [],
  propertyList: [],
  searchMembersList: [],
  planInfo: [],
  memberInfo: [],
  searchPlanList: [],
  searchManagements: [],
  searchOwnersGroup: [],
  searchOwners: [],
  ownerInfo: [],
  savedFilter: {},
  isOpenCorpWizard: true,
  view: "",
  showLoader: false,
  searchPropertyList: [],
  staticPropertyInfo: [],
  filterCategoryData: [],
  searchedInncodes: [],
  userAssociatedInncode: []
};

export const fetchFilterPropertyInfo = createAsyncThunk(`${SLICE}/fetchFilterPropertyInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getPropertyInfo, {}, thunkApi, [], { inncodes: data?.innCodesValue })
);

export const fetchUserSearchedInncodeAsync = createAsyncThunk(
  `${SLICE}/fetchUserSearchedInncodeAsync`,
  (data: {}, thunkApi) => apiRequest(LIBRARY_SERVICE_METHOD.getInncode, {}, thunkApi, [], { searchText: data })
);

export const fetchManagementcOInfo = createAsyncThunk(`${SLICE}/fetchManagementcOInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getManagementCompany, {}, thunkApi, [], { searchText: data })
);

export const fetchOwnerInfo = createAsyncThunk(`${SLICE}/fetchOwnerInfo`, (data: any, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getOwnerGroups, {}, thunkApi, [], { searchText: data })
);

export const fetchmembersInfo = createAsyncThunk(`${SLICE}/fetchmembersInfo`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], { ...data })
);

export const fetchFilterSelectedPlansAsync = createAsyncThunk(
  `${SLICE}/fetchFilterSelectedPlansAsync`,
  (data: any, thunkApi) =>
    apiRequest(LIBRARY_SERVICE_METHOD.postPlans, data?.payload, thunkApi, [], {
      externalHltId: data?.externalHltId || ""
    })
);

export const fetchFilterCategories = createAsyncThunk(`${SLICE}/fetchFilterCategories`, (thunkApi: any) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getFilterCategories, {}, thunkApi)
);

export const fetchCorpSavedFilters = createAsyncThunk(`${SLICE}/fetchCorpSavedFilters`, async (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getFilters, {}, thunkApi, [], {
    types: data?.types
  })
);

export const saveFilterByType = createAsyncThunk(`${SLICE}/saveFilterByType`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postFilters, data, thunkApi)
);

export const saveDefaultFilters = createAsyncThunk(`${SLICE}/saveDefaultFilters`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postFilters, data, thunkApi)
);

export const createFilterByType = createAsyncThunk(`${SLICE}/createFilterByType`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postFilters, data, thunkApi)
);

export const updateCorpSavedFilters = createAsyncThunk(`${SLICE}/updateCorpSavedFilters`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.putFilters, data, thunkApi)
);

export const updateStaticList = createAsyncThunk(`${SLICE}/updateStaticList`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.putFilters, data, thunkApi)
);

export const duplicateFilter = createAsyncThunk(`${SLICE}/duplicateFilter`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postDuplicateFilter, data, thunkApi)
);

export const updateCorpSavedFiltersConsultant = createAsyncThunk(
  `${SLICE}/updateCorpSavedFiltersConsultant`,
  (data: any, thunkApi) => apiRequest(USER_PLAN_SERVICE_METHOD.patchInncodeGroup, data.payload, thunkApi, [data.listId])
);

export const fetchPropertylist = createAsyncThunk(`${SLICE}/fetchPropertylist`, async (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getFilters, data.payload, thunkApi, [], data)
);

export const FiltersSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setCheckedDynamicInncode: (state, action) => {
      state.propertyList = [
        ...state.propertyList,
        { innCode: action?.payload?.innCode, propName: action?.payload?.propName }
      ];
    },
    setCheckedPlanInfo: (state, action) => {
      state.planInfo = [...state.planInfo, { id: action?.payload?.id, name: action?.payload?.name }];
    },
    setCheckedOwnerInfo: (state, action) => {
      state.ownerInfo = [
        ...state.ownerInfo,
        { id: action?.payload?.id, name: action?.payload?.name, email: action?.payload?.email }
      ];
    },
    setMemberInfo: (state, action) => {
      state.memberInfo = [
        ...state.memberInfo,
        {
          hltId: action?.payload?.hltId,
          displayName: action?.payload?.displayName,
          email: action?.payload?.email
        }
      ];
    },
    setSavedFilter: (state, action) => {
      const { name, data } = action.payload || {};
      const filterList = state.savedFilter[name] || [];
      const lastFlaggedIndex = filterList.findLastIndex((item: any) => item.isFlagged === "Y");
      filterList.splice(lastFlaggedIndex > -1 ? lastFlaggedIndex + 1 : 0, 0, data);
      state.savedFilter = { ...state.savedFilter, [name]: filterList };
    },
    setSavedFilterByType: (state, action) => {
      state.savedFilter[action.payload.type] = action.payload.data;
    },
    searchUserAssociatedInncodeLocally: (state, action) => {
      state.searchedInncodes = state?.userAssociatedInncode?.filter((item: any) =>
        item?.innCode?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase())
      );
    },
    searchOwners: (state, action) => {
      state.searchOwners = action?.payload?.userList
        ?.filter(
          (item: any) =>
            item?.email?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase()) ||
            item?.displayName?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase()) ||
            item?.firstName?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase()) ||
            item?.lastName?.toLowerCase()?.includes(action?.payload?.searchText?.toLowerCase())
        )
        ?.map((item2: any) => {
          return {
            id: item2?.hltId,
            name: getUserNameWithConditionalCheck(item2?.displayName, item2?.firstName, item2?.lastName),
            email: item2?.email
          };
        });
    },
    setView: (state, action) => {
      state.view = action?.payload;
    },
    setLoader: (state, action) => {
      state.showLoader = action.payload;
    },

    setStaticPropertyInfo: (state, action) => {
      const { id, name } = action.payload;
      state.staticPropertyInfo = [...state.staticPropertyInfo, { id: id, name: name }];
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterPropertyInfo.fulfilled, (state, action: any) => {
        const propertyList = Object.values(action?.payload).filter((value) => value !== 200);
        if (action?.meta.arg.isUserAssociated)
          state.userAssociatedInncode = propertyList?.map((res: any) => {
            return { innCode: res?.innCode, name: res?.propName, brandCode: res?.brandCode, brandName: res?.brandName };
          });
        else state.propertyList = [...state.propertyList, ...propertyList];
      })
      .addCase(fetchUserSearchedInncodeAsync.fulfilled, (state, action: any) => {
        state.searchedInncodes = action.payload.properties;
      })
      .addCase(fetchUserSearchedInncodeAsync.rejected, (state, action: any) => {
        state.searchedInncodes = [];
      })
      .addCase(fetchFilterSelectedPlansAsync.fulfilled, (state, action: any) => {
        if (action?.meta.arg.isSearched) state.searchPlanList = action?.payload?.plans;
        else state.planInfo = [...state.planInfo, ...action.payload.plans];
      })
      .addCase(fetchManagementcOInfo.fulfilled, (state, action: any) => {
        state.searchManagements = Object.values(action?.payload).filter((value) => value !== 200);
      })
      .addCase(fetchOwnerInfo.fulfilled, (state, action: any) => {
        state.searchOwnersGroup = Object.values(action?.payload).filter((value) => value !== 200);
      })
      .addCase(fetchmembersInfo.fulfilled, (state, action: any) => {
        if (action?.meta?.arg?.type === "owner")
          state.ownerInfo = [
            ...(state?.ownerInfo ?? []),
            ...(action.payload?.users?.map((item: any) => {
              return {
                id: item?.hltId,
                name: getUserNameWithConditionalCheck(item?.displayName, item?.firstName, item?.lastName),
                email: item?.email
              };
            }) ?? [])
          ];
        else state.searchMembersList = action?.payload?.users;
      })
      .addCase(fetchFilterCategories.fulfilled, (state, action: any) => {
        state.filterCategories = action.payload?.filter;
        state.filterCategoryData = state.filterCategories
          ?.find((item: any) => item?.key === "filterIds")
          ?.filters?.flatMap((item2: any) => item2?.filters || []);
      })
      .addCase(fetchCorpSavedFilters.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(fetchCorpSavedFilters.fulfilled, (state, action: any) => {
        state.savedFilter = action.payload;
        if (action?.meta?.arg?.isModalClose || action.payload.default.length) {
          state.isOpenCorpWizard = false;
        }
        state.showLoader = false;
      })
      .addCase(fetchCorpSavedFilters.rejected, (state) => {
        state.showLoader = false;
      })

      .addCase(saveFilterByType.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(updateStaticList.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(updateStaticList.fulfilled, (state) => {
        state.showLoader = false;
      })
      .addCase(updateStaticList.rejected, (state) => {
        state.showLoader = false;
      })

      .addCase(saveDefaultFilters.pending, (state) => {
        state.showLoader = true;
      })
      .addCase(saveDefaultFilters.rejected, (state) => {
        state.showLoader = false;
      })
      .addCase(saveDefaultFilters.fulfilled, (state, action: any) => {
        state.showLoader = false;
      })

      .addCase(saveFilterByType.fulfilled, (state, action: any) => {
        state.showLoader = false;
        state.savedFilter = { ...state.savedFilter, ...action.payload };
      })
      .addCase(saveFilterByType.rejected, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(createFilterByType.pending, (state, action: any) => {
        state.showLoader = true;
      })
      .addCase(createFilterByType.fulfilled, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(createFilterByType.rejected, (state, action: any) => {
        state.showLoader = false;
      })
      .addCase(updateCorpSavedFiltersConsultant.fulfilled, (state, action: any) => {
        state.showLoader = false;
        state.savedFilter = { ...state.savedFilter, ...action.payload };
      })

      .addCase(fetchPropertylist.fulfilled, (state, action: any) => {
        if (action?.meta?.arg?.fetchIdToValue) {
          state.staticPropertyInfo = action.payload?.static;
        } else {
          state.searchPropertyList = action.payload?.static;
        }
        state.showLoader = false;
      })

      .addMatcher(
        isFulfilled(deleteMyList, deleteSCList, duplicateFilter, updateCorpSavedFilters),
        (state, action: any) => {
          if ("static" in action.payload) {
            state.savedFilter.static = action.payload.static;
          }
          if ("dynamic" in action.payload) {
            state.savedFilter.dynamic = action.payload.dynamic;
          }
          if ("consultant" in action.payload) {
            state.savedFilter.consultant = action.payload.consultant;
          }

          state.showLoader = false;
        }
      )

      .addMatcher(
        isRejected(deleteMyList, deleteSCList, duplicateFilter, fetchPropertylist, updateCorpSavedFilters),
        (state) => {
          state.showLoader = false;
        }
      )
      .addMatcher(
        isPending(deleteMyList, deleteSCList, duplicateFilter, fetchPropertylist, updateCorpSavedFilters),
        (state) => {
          state.showLoader = true;
        }
      );
  }
});

export const {
  setCheckedDynamicInncode,
  setCheckedPlanInfo,
  setView,
  searchOwners,
  setCheckedOwnerInfo,
  setSavedFilter,
  setSavedFilterByType,
  setLoader,
  setSearchText,
  setMemberInfo,
  setStaticPropertyInfo,
  searchUserAssociatedInncodeLocally
} = FiltersSlice.actions;

export default FiltersSlice.reducer;
